import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Lock, ShieldCheck } from "lucide-react";
import { EpicomLogo } from "@/components/brand/Logo";
import { useAuth } from "@/contexts/AuthContext";

/** Admin login page — single Google sign-in button (Emergent-managed). */
export default function Login() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  // If we already have a valid session, skip the login screen.
  useEffect(() => {
    if (loading) return;
    if (user?.is_admin) {
      navigate("/admin", { replace: true });
    } else if (user) {
      // Signed in but not admin — kick back to home
      navigate("/", { replace: true });
    }
  }, [user, loading, navigate]);

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  if (loading || user?.is_admin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg-primary">
        <div className="font-mono text-xs tracking-[0.25em] text-steel-light uppercase">
          {user?.is_admin ? "Welcome back…" : "Checking session…"}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-bg-primary px-6">
      <Link to="/" className="absolute top-8 left-8 flex items-center gap-3">
        <EpicomLogo className="h-10 w-auto" />
      </Link>

      <div className="w-full max-w-md border border-steel-dark bg-bg-secondary p-10">
        <div className="font-mono text-xs tracking-[0.25em] text-signal uppercase mb-6 flex items-center gap-3">
          <span className="inline-block w-8 h-[2px] bg-signal" />
          Restricted Access
        </div>
        <h1 className="heading text-3xl md:text-4xl text-white uppercase mb-3 leading-tight">
          Admin Login.
        </h1>
        <p className="text-steel-light text-sm leading-relaxed mb-8">
          Sign in with the authorized Google account to manage job postings and toggle the careers section.
        </p>

        <button
          onClick={handleGoogleLogin}
          data-testid="login-google-btn"
          className="w-full inline-flex items-center justify-center gap-3 bg-white hover:bg-steel-light text-bg-primary font-bold uppercase tracking-wider px-6 py-4 transition-colors"
        >
          <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.13 4.13 0 0 1-1.79 2.71v2.26h2.9c1.7-1.57 2.69-3.88 2.69-6.61z" />
            <path fill="#34A853" d="M9 18c2.43 0 4.46-.8 5.95-2.18l-2.9-2.26c-.81.54-1.83.86-3.05.86-2.34 0-4.33-1.58-5.04-3.71H.96v2.33A9 9 0 0 0 9 18z" />
            <path fill="#FBBC05" d="M3.96 10.71A5.41 5.41 0 0 1 3.68 9c0-.59.1-1.17.28-1.71V4.96H.96A9 9 0 0 0 0 9c0 1.45.35 2.82.96 4.04l3-2.33z" />
            <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58A8.97 8.97 0 0 0 9 0 9 9 0 0 0 .96 4.96l3 2.33C4.67 5.16 6.66 3.58 9 3.58z" />
          </svg>
          Sign in with Google
        </button>

        <div className="mt-6 flex items-start gap-3 text-xs text-steel-light leading-relaxed">
          <ShieldCheck size={16} className="text-signal flex-shrink-0 mt-0.5" />
          <p>
            Only the authorized owner email can access the admin panel. All other Google accounts will be redirected back to the homepage.
          </p>
        </div>

        <Link
          to="/"
          data-testid="login-back-home"
          className="mt-8 inline-flex items-center gap-2 font-mono text-xs tracking-[0.2em] uppercase text-steel-light hover:text-signal transition-colors"
        >
          <Lock size={12} /> ← Back to site
        </Link>
      </div>
    </div>
  );
}
